import type { ColumnDef, Row, Table } from "@tanstack/react-table";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "../ui";
import { PenIcon, TrashIcon } from "lucide-react";
import { toast } from "sonner";
import DataTable from "./table";
import { getActionColDefs } from "~/utils/table-util";
import type { TableActionEventType } from "~/types/ui";

export function SimpleTable<T>({
  data,
  onEdit,
  onRemove,
  colDef,
  singleEditable = false,
  multiEditable = false,
  removable = false,
  pageSizes = [15, 25, 50, 100],
}: {
  data: T[];
  colDef: ColumnDef<any>[];
  onEdit?: (val: T[]) => void;
  onRemove?: (val: T[]) => void;
  singleEditable?: boolean;
  multiEditable?: boolean;

  removable?: boolean;
  pageSizes?: number[];
}) {
  const { t } = useTranslation();
  const [table, setTable] = useState<Table<any>>();
  const cols = colDef.concat(
    ...getActionColDefs(
      handleActionEvent,
      singleEditable || multiEditable,
      removable,
      true,
    ),
  );

  const [rowSelections, setRowSelections] = useState<{
    [key: string]: boolean;
  }>({});

  async function handleEdit(row?: T) {
    if (table === undefined) {
      toast.error(t("common.errors.exportNoTable"));
      return;
    }
    const selected = row
      ? [row]
      : (table?.getSelectedRowModel().flatRows.map((r) => r.original) as T[]);
    if (onEdit) onEdit(selected);
  }

  async function handleRemove(row?: T) {
    if (table === undefined) {
      toast.error(t("common.errors.exportNoTable"));
      return;
    }
    const selected = row
      ? [row]
      : (table?.getSelectedRowModel().flatRows.map((r) => r.original) as T[]);
    if (onRemove) onRemove(selected);
    setRowSelections({});
  }

  async function handleActionEvent(event: TableActionEventType, row: Row<any>) {
    if (event === "edit") {
      await handleEdit(row.original);
    }
    if (event === "delete") {
      await handleRemove(row.original);
    }
  }

  return (
    <div>
      <DataTable
        data={data}
        columns={cols}
        selectable
        externalSelection={rowSelections}
        onSelectionChange={(rs) => {
          setRowSelections(rs);
        }}
        tableReady={setTable}
        pageSizes={pageSizes}
      />
      {rowSelections && Object.keys(rowSelections).length > 0 && (
        <>
          {multiEditable && (
            <Button
              onClick={() => handleEdit()}
              variant={"outline"}
              aria-label="Edit Selected"
              type="button"
            >
              <PenIcon className="h-[1.2rem] w-[1.2rem]" />
              {t("common.editSelected")}
            </Button>
          )}
          {removable && (
            <Button
              onClick={() => handleRemove()}
              variant={"outline"}
              aria-label="Remove Selected"
              type="button"
            >
              <TrashIcon className="h-[1.2rem] w-[1.2rem]" />
              {t("common.deleteSelected")}
            </Button>
          )}
        </>
      )}
    </div>
  );
}
